import React from "react";
import { Descriptions, Tag, Typography } from "antd";
import { format, fromUnixTime } from "date-fns";
import formatMoney from "../../../utils/formatMoney";

const { Paragraph } = Typography;

/** Mã màn hình đặt trong ClickLog.source → tên tiếng Việt.
 *  Đồng bộ với mobile/src/shared/constants/link-source.ts và frontend/src/constants/link-source.js. */
const LINK_SOURCE_LABEL = {
    pd: "Chi tiết sản phẩm",
    sr: "Kết quả tìm kiếm",
    hm: "Trang chủ",
    tr: "Xu hướng",
    sv: "Đã lưu",
    rv: "Vừa xem",
    pw: "Theo dõi giá",
    vc: "Voucher",
    lc: "Rải link",
    go: "Đơn ảo",
    rd: "Chuyển hướng",
    ec: "Danh mục",
    sx: "Shopee Xu",
    vd: "Shopee Video",
    ex: "Tiện ích trình duyệt",
    rw: "Đợt thưởng",
    fs: "Flash sale",
};

const DB_STATUS_TAG = {
    1: { color: "orange", text: "Chờ duyệt" },
    2: { color: "green", text: "Đã duyệt" },
    3: { color: "default", text: "Đã huỷ" },
    4: { color: "orange", text: "Chờ duyệt" },
};

const money = (v) => formatMoney.format(Number(v) || 0);
const dt = (ts) => (ts ? format(fromUnixTime(ts), "HH:mm:ss dd/MM/yyyy") : "—");
const dash = <span className="text-content-3">—</span>;

/** Tag kênh app/web dùng chung cho cả block đơn và block ClickLog. */
const channelTag = (channel) =>
    channel === "app" ? <Tag color="purple">App</Tag>
        : channel === "web" ? <Tag color="cyan">Web</Tag>
            : dash;

/** Chuỗi dài (subid, clickId, link) — hiện monospace + nút copy để NV dán sang tool đối soát. */
const copyable = (v) =>
    v ? <Paragraph className="!mb-0 text-xs font-mono" copyable={{ text: v }}>{v}</Paragraph> : dash;

const sourceLabel = (s) => (s ? `${LINK_SOURCE_LABEL[s] || "Không rõ"} (${s})` : "—");

/**
 * Block 1 — dữ liệu hệ thống của đơn (bảng Orders).
 * Trả lời được: đơn thuộc user nào, kênh nào, hoa hồng chia ra sao, đã trả tiền chưa,
 * và subid gốc để đối chiếu tay với báo cáo của sàn.
 */
export function OrderSystemPanel({ order }) {
    if (!order) return null;
    const items = [
        { key: "userId", label: "User ID", children: order.userId || dash },
        {
            key: "channel",
            label: "Kênh",
            children: (
                <span className="flex items-center gap-2">
                    {channelTag(order.channel)}
                    {order.x2 && <Tag color="red">x{order.x2Multiplier || 2}</Tag>}
                </span>
            ),
        },
        { key: "campId", label: "Sàn", children: order.campId || dash },
        { key: "cookiePlatform", label: "TK sàn", children: order.cookiePlatform || dash },
        {
            key: "dbStatus",
            label: "Trạng thái hệ thống",
            children: (() => {
                const t = DB_STATUS_TAG[order.conversionStatus];
                return t ? <Tag color={t.color}>{t.text}</Tag> : (order.conversionStatus ?? dash);
            })(),
        },
        { key: "orderStatus", label: "Order status", children: order.orderStatus || dash },
        { key: "commission", label: "Hoa hồng sàn", children: money(order.commission) },
        { key: "userCommission", label: "Hoàn cho user", children: money(order.userCommission) },
        {
            key: "refCommission",
            label: "Hoa hồng ref",
            children: order.refId
                ? <span>{money(order.refCommission)} <span className="text-content-3">(ref {order.refId})</span></span>
                : dash,
        },
        {
            key: "payedToUser",
            label: "Đã thanh toán",
            children: order.payedToUser
                ? <span><Tag color="green">Rồi</Tag>{order.payTime ? dt(order.payTime) : null}</span>
                : <Tag>Chưa</Tag>,
        },
        { key: "purchaseTime", label: "Giờ đặt hàng", children: dt(order.purchaseTime) },
        { key: "clickTime", label: "Giờ click (sàn báo)", children: dt(order.clickTime) },
        { key: "completeTime", label: "Giờ hoàn thành", children: dt(order.completeTime) },
        { key: "internalSource", label: "Internal source", children: order.internal_source || dash },
        { key: "subid", label: "Subid", span: 2, children: copyable(order.subid) },
        {
            key: "clickId",
            label: "Click ID",
            span: 2,
            // clickId chỉ được job conversion V2 điền; đơn do realApi/v1 tạo thì cột này trống
            // nhưng sub3 vẫn CHÍNH LÀ clickId → ghi rõ để NV không tưởng đơn mất dấu vết.
            children: order.clickId
                ? copyable(order.clickId)
                : order.sub3
                    ? <span className="flex items-center gap-2">{copyable(order.sub3)}<Tag color="gold">suy từ sub3</Tag></span>
                    : dash,
        },
    ];
    return <Descriptions bordered size="small" column={2} title="Thông tin hệ thống" items={items} />;
}

/**
 * Block 2 — ClickLog gốc: link affiliate này được tạo Ở ĐÂU.
 * Đây là chỗ giải thích vì sao đơn ra kênh web dù user hay dùng app (Shopee tính last-click:
 * link tạo sau cùng trước lúc đặt hàng mới là link ăn đơn).
 */
export function OrderClickLogPanel({ order, clickLog }) {
    if (!clickLog) {
        return (
            <Descriptions
                bordered
                size="small"
                column={1}
                title="Nguồn click"
                style={{ marginTop: 16 }}
                items={[{
                    key: "empty",
                    label: "Kết quả",
                    children: (
                        <span className="text-content-3">
                            Không tìm thấy ClickLog cho đơn này — link tạo trước khi bật ghi log,
                            hoặc subid không phải link do hệ thống sinh.
                        </span>
                    ),
                }]}
            />
        );
    }

    // Lệch kênh giữa ClickLog và Orders = dấu hiệu dữ liệu hỏng (channel của đơn suy từ sub2
    // trong subid, độc lập với ClickLog) → phải cho NV thấy ngay chứ không giấu.
    const channelMismatch = clickLog.channel && order?.channel && clickLog.channel !== order.channel;
    const userMismatch =
        clickLog.userId && order?.userId && String(clickLog.userId) !== String(order.userId);

    const items = [
        {
            key: "channel",
            label: "Kênh tạo link",
            children: (
                <span className="flex items-center gap-2">
                    {channelTag(clickLog.channel)}
                    {channelMismatch && <Tag color="red">lệch với kênh đơn</Tag>}
                </span>
            ),
        },
        { key: "source", label: "Màn hình", children: sourceLabel(clickLog.source) },
        { key: "appVersion", label: "Phiên bản", children: clickLog.appVersion || dash },
        { key: "os", label: "Thiết bị", children: clickLog.os || dash },
        { key: "ip", label: "IP", children: clickLog.ip ? <span className="font-mono text-xs">{clickLog.ip}</span> : dash },
        { key: "createdAt", label: "Giờ tạo link", children: dt(clickLog.created_at) },
        {
            key: "clickUser",
            label: "User tạo link",
            children: (
                <span className="flex items-center gap-2">
                    {clickLog.userId || dash}
                    {userMismatch && <Tag color="red">lệch với user đơn</Tag>}
                </span>
            ),
        },
        { key: "platform", label: "Sàn", children: clickLog.platform || dash },
        {
            key: "targetUrl",
            label: "Link gốc",
            span: 2,
            children: clickLog.targetUrl
                ? (
                    <a
                        href={clickLog.targetUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs break-all text-blue-600"
                    >
                        {clickLog.targetUrl}
                    </a>
                )
                : dash,
        },
    ];

    return <Descriptions bordered size="small" column={2} title="Nguồn click" style={{ marginTop: 16 }} items={items} />;
}
